import { useState, useEffect, useCallback } from 'react';
import { supabase, missingConfig } from './supabaseClient';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import DisplayToggles from './components/DisplayToggles';
import { useNumberFormat } from './numberFormat';
import Dashboard from './components/Dashboard';
import Menu from './components/Menu';
import RateCardPage from './components/RateCardPage';
import DirectReportsPage from './components/DirectReportsPage';
import UsersPage from './components/UsersPage';
import { DASHBOARDS, canOpen } from './dashboards';

export default function App() {
  if (missingConfig.length) return <ConfigError missing={missingConfig} />;
  return <AuthedApp />;
}

function AuthedApp() {
  useNumberFormat(); // re-render the whole tree when the number format changes
  const [session,   setSession]   = useState(null);
  const [apps,      setApps]      = useState(null); // null=checking, string[] of allowed_users.apps
  const [loading,   setLoading]   = useState(true);

  // Load the app flags for the logged-in user (empty = not on the allowlist)
  const loadApps = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_my_apps');
    setApps(error ? [] : (data || []));
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadApps();
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) loadApps(); // keep current apps while refreshing so the UI doesn't flash or lose state
      else { setApps(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, [loadApps]);

  // Once apps resolve, stop loading
  useEffect(() => {
    if (apps !== null) setLoading(false);
  }, [apps]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (loading) return <Spinner />;
  if (!session) return <Login />;
  if (apps === null) return <Spinner />;
  if (apps.length === 0) return <AccessDenied email={session.user.email} onSignOut={handleSignOut} />;

  const guard = (id, element) => {
    const d = DASHBOARDS.find(x => x.id === id);
    return canOpen(apps, d) ? element : <Navigate to="/" replace />;
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Menu session={session} apps={apps} onSignOut={handleSignOut} />} />
        <Route path="/dashboard" element={guard('main', <Dashboard session={session} onSignOut={handleSignOut} />)} />
        <Route path="/direct-reports" element={guard('directreports', <DirectReportsPage session={session} onSignOut={handleSignOut} />)} />
        <Route path="/rate-card" element={guard('ratecard', <RateCardPage session={session} onSignOut={handleSignOut} />)} />
        <Route path="/users" element={guard('users', <UsersPage session={session} onSignOut={handleSignOut} />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:16 }}>
      <div style={{ width:32, height:32, borderRadius:'50%', border:'3px solid var(--border)', borderTopColor:'var(--green)', animation:'spin 0.8s linear infinite' }} />
      <p style={{ color:'var(--text2)', fontSize:14 }}>Loading…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function AccessDenied({ email, onSignOut }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'var(--bg)' }}>
      <DisplayToggles floating />
      <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:16, padding:'40px 48px', textAlign:'center', maxWidth:420 }}>
        <div style={{ fontSize:32, marginBottom:16 }}>🔒</div>
        <h2 style={{ fontSize:20, fontWeight:700, marginBottom:8 }}>Access not granted</h2>
        <p style={{ color:'var(--text2)', fontSize:14, marginBottom:4 }}>{email}</p>
        <p style={{ color:'var(--text2)', fontSize:14, marginBottom:24 }}>Your account hasn't been added to the INPULSE Core Operations allowlist. Contact Diego to request access.</p>
        <button onClick={onSignOut} style={{ background:'var(--card2)', border:'1px solid var(--border)', color:'var(--text)', borderRadius:8, padding:'10px 24px', cursor:'pointer', fontWeight:600, fontSize:14 }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function ConfigError({ missing }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'var(--bg)' }}>
      <DisplayToggles floating />
      <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:16, padding:'40px 48px', maxWidth:480 }}>
        <h2 style={{ fontSize:20, fontWeight:700, marginBottom:8 }}>Site is missing configuration</h2>
        <p style={{ color:'var(--text2)', fontSize:14, marginBottom:12 }}>This build was made without:</p>
        <ul style={{ fontFamily:'DM Mono,monospace', fontSize:13, marginBottom:16, paddingLeft:20 }}>
          {missing.map(m => <li key={m}>{m}</li>)}
        </ul>
        <p style={{ color:'var(--text2)', fontSize:13 }}>Add them in Netlify → Site configuration → Environment variables, then redeploy (Deploys → Trigger deploy → Clear cache and deploy site).</p>
      </div>
    </div>
  );
}
